/* eslint-disable no-param-reassign */
import ridershipService from '../../services/ridershipService';
import downloadRidershipReport from '../../helpers/ridershipReport';
import { track } from '../../helpers/rum';
import groupStopsByRegion from '../../helpers/ridershipStops';

// Latest trips / stops request. Filters can change while one is in flight (a preset arriving, a quick
// second pick), and responses can come back out of order; only the latest request may write the state.
let latestTripsRequest = 0;
let latestStopsRequest = 0;

/**
 * Sort array alphabetically by key with Turkish locale support
 */
function sortAlphabetically(array, key) {
  if (!Array.isArray(array) || array.length === 0) return array;
  return [...array].sort((a, b) => {
    const valueA = a[key] || '';
    const valueB = b[key] || '';
    return valueA.localeCompare(valueB, 'tr-TR');
  });
}

/**
 * Extract hour from time string like "06:40 AM" or "14:30"
 */
function extractHour(timeStr) {
  if (!timeStr) return 0;
  // Handle HH:mm format
  const match24 = timeStr.match(/^(\d{1,2}):/);
  if (match24) return parseInt(match24[1], 10);
  return 0;
}

/**
 * Map dayOfWeek string to sortable index
 */
const DAY_ORDER = {
  MONDAY: 0,
  TUESDAY: 1,
  WEDNESDAY: 2,
  THURSDAY: 3,
  FRIDAY: 4,
  SATURDAY: 5,
  SUNDAY: 6,
};

function dayIndex(dayStr) {
  if (!dayStr) return 7;
  return DAY_ORDER[dayStr.toUpperCase()] ?? 7;
}

/**
 * Generate a consistent colour for a route name.
 * Palette is the canonical flexiLoop "Route Colors" ordered list, augmented
 * with a couple of distinct hues that aren't covered by it.
 *
 * Design sources:
 *  - flexiLoop_Route_Colors_Ordered (20 colours, primary)
 *    https://app.zeplin.io/project/608a8f0725110c335b531286/screen/68542a5b771af8d333a795df
 *  - flexiShuttle_Routecolors (additional unique hues)
 *    https://app.zeplin.io/project/608a8f0725110c335b531286/screen/6893004374de8b30739671d8
 *  - Earlier ridership chart design (additional unique hues)
 *    https://app.zeplin.io/project/60986c4284a9503b87bf7f92/screen/69c23dd0c33d691217211518
 */
const ROUTE_PALETTE = [
  // flexiLoop_Route_Colors_Ordered (canonical, in order)
  '#3F51B5', // Indigo Blue
  '#FFC107', // Amber
  '#EC407A', // Rose Pink
  '#03A9F4', // Sky Blue
  '#008B8B', // Dark Cyan
  '#8E24AA', // Violet Purple
  '#F57C00', // Orange Rust
  '#607D8B', // Steel Gray
  '#E97451', // Burnt Sienna
  '#6A5ACD', // Slate Blue
  '#2E7D32', // Forest Green
  '#FF6347', // Tomato
  '#008080', // Teal
  '#A4DE02', // Lime
  '#D100D1', // Magenta
  '#FF6600', // Bright Orange
  '#E60026', // Crimson Red
  '#556B2F', // Dark Olive Green
  '#FF1493', // Hot Pink
  '#6A00FF', // Deep Purple
  // Distinct additions not covered above
  '#B95D00', // Earth Brown (from flexiShuttle_Routecolors)
  '#027DB4', // Mid Blue (from earlier ridership chart palette)
];

function routeColor(index) {
  return ROUTE_PALETTE[index % ROUTE_PALETTE.length];
}

export default {
  namespaced: true,

  state: {
    // Raw API data
    rawTrips: [],
    rawStops: [],
    availableRoutes: [],
    availableStops: [],

    // Route colour mapping: { routeName: '#hex' }
    routeColorMap: {},

    // Filters
    filters: {
      dateRange: {
        type: 'thisMonth',
        fromDate: null,
        toDate: null,
        startDay: null,
        endDay: null,
      },
      routeIds: [],
      direction: 'ALL',
      stopIds: [],
      fromTime: null,
      toTime: null,
      // "by Route" card view mode: 'timeOfDay' | 'dayOfWeek' | 'date'
      routeViewMode: 'timeOfDay',
      // "by Stop" card metric: 'sum' | 'on' | 'off'
      stopMetric: 'sum',
      stopSortAsc: true,
      // Period toggle: 'timeOfDay' | 'dayOfWeek' | 'date'
      period: 'timeOfDay',
      // Reference time: 'scheduled' | 'actual'
      referenceTime: 'scheduled',
    },

    loading: {
      trips: false,
      stops: false,
      routes: false,
      stopsList: false,
    },
    error: null,
  },

  mutations: {
    SET_RAW_TRIPS(state, data) {
      state.rawTrips = Array.isArray(data) ? data : [];
    },
    SET_RAW_STOPS(state, data) {
      state.rawStops = Array.isArray(data) ? data : [];
    },
    SET_AVAILABLE_ROUTES(state, routes) {
      state.availableRoutes = sortAlphabetically(routes, 'name');
      // Build colour map
      const colorMap = {};
      state.availableRoutes.forEach((r, i) => {
        colorMap[r.name] = routeColor(i);
      });
      state.routeColorMap = colorMap;
    },
    SET_AVAILABLE_STOPS(state, stops) {
      state.availableStops = sortAlphabetically(stops, 'name');
    },
    SET_LOADING(state, { key, value }) {
      state.loading[key] = value;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    UPDATE_DATE_RANGE(state, dateRange) {
      state.filters.dateRange = dateRange;
    },
    UPDATE_ROUTE_IDS(state, routeIds) {
      state.filters.routeIds = Array.isArray(routeIds) ? routeIds : [];
    },
    UPDATE_DIRECTION(state, direction) {
      state.filters.direction = direction || 'ALL';
    },
    UPDATE_STOP_IDS(state, stopIds) {
      state.filters.stopIds = Array.isArray(stopIds) ? stopIds : [];
    },
    UPDATE_FROM_TIME(state, fromTime) {
      state.filters.fromTime = fromTime;
    },
    UPDATE_TO_TIME(state, toTime) {
      state.filters.toTime = toTime;
    },
    UPDATE_ROUTE_VIEW_MODE(state, mode) {
      state.filters.routeViewMode = mode;
    },
    UPDATE_STOP_METRIC(state, metric) {
      state.filters.stopMetric = metric;
    },
    TOGGLE_STOP_SORT(state) {
      state.filters.stopSortAsc = !state.filters.stopSortAsc;
    },
    UPDATE_PERIOD(state, period) {
      state.filters.period = period;
    },
    UPDATE_REFERENCE_TIME(state, refTime) {
      state.filters.referenceTime = refTime;
    },
    // Also invalidates requests still in flight, so a response for the cleared view can't land afterwards.
    CLEAR_DATA(state) {
      latestTripsRequest += 1;
      latestStopsRequest += 1;
      state.rawTrips = [];
      state.rawStops = [];
      state.loading.trips = false;
      state.loading.stops = false;
      state.error = null;
    },
  },

  actions: {
    /**
     * Fetch available routes for filter dropdowns
     */
    // Returns the list it loaded: both cards load into the shared availableRoutes, so a card that read it
    // after awaiting could get the other card's list.
    async fetchRoutes({ commit, state }, { startDay, endDay }) {
      if (!startDay || !endDay) return undefined;

      commit('SET_LOADING', { key: 'routes', value: true });
      try {
        const routes = await ridershipService.getRoutes({ startDay, endDay });
        commit('SET_AVAILABLE_ROUTES', routes || []);
        return state.availableRoutes;
      } catch (error) {
        console.error('Error fetching ridership routes:', error);
        return undefined;
      } finally {
        commit('SET_LOADING', { key: 'routes', value: false });
      }
    },

    /**
     * Fetch available stops for filter dropdowns
     */
    async fetchStopsList({ commit }, { startDay, endDay, routeIds }) {
      if (!startDay || !endDay) return;

      commit('SET_LOADING', { key: 'stopsList', value: true });
      try {
        const stops = await ridershipService.getStopsList({
          startDay,
          endDay,
          routeIds: routeIds && routeIds.length > 0 ? routeIds : undefined,
        });
        commit('SET_AVAILABLE_STOPS', stops || []);
      } catch (error) {
        console.error('Error fetching ridership stops list:', error);
      } finally {
        commit('SET_LOADING', { key: 'stopsList', value: false });
      }
    },

    /**
     * Fetch trip-level data for "Ridership by Route" card
     */
    // An empty result without asking the backend (a card whose picked route groups have no routes); also
    // drops a request still in flight, which would otherwise land afterwards.
    clearTrips({ commit }) {
      latestTripsRequest += 1;
      commit('SET_RAW_TRIPS', []);
      commit('SET_LOADING', { key: 'trips', value: false });
    },
    clearStops({ commit }) {
      latestStopsRequest += 1;
      commit('SET_RAW_STOPS', []);
      commit('SET_LOADING', { key: 'stops', value: false });
    },

    async fetchTrips({ commit }, { startDay, endDay, routeIds, direction, fromTime, toTime, workingTypes }) {
      if (!startDay || !endDay) return;

      latestTripsRequest += 1;
      const request = latestTripsRequest;
      const isLatest = () => request === latestTripsRequest;
      commit('SET_LOADING', { key: 'trips', value: true });
      commit('SET_ERROR', null);
      try {
        const trips = await ridershipService.getTrips({
          startDay,
          endDay,
          direction,
          routeIds: routeIds && routeIds.length > 0 ? routeIds : undefined,
          fromTime: fromTime || undefined,
          toTime: toTime || undefined,
          workingTypes,
        });
        if (isLatest()) commit('SET_RAW_TRIPS', trips);
      } catch (error) {
        if (isLatest()) commit('SET_ERROR', error.message || 'Failed to fetch trips');
        console.error('Error fetching ridership trips:', error);
      } finally {
        if (isLatest()) commit('SET_LOADING', { key: 'trips', value: false });
      }
    },

    /**
     * Fetch aggregated stop data for "Ridership by Stop" card
     */
    async fetchStops({ commit }, { startDay, endDay, routeIds, stopIds, fromTime, toTime, workingTypes }) {
      if (!startDay || !endDay) return;

      latestStopsRequest += 1;
      const request = latestStopsRequest;
      const isLatest = () => request === latestStopsRequest;
      commit('SET_LOADING', { key: 'stops', value: true });
      commit('SET_ERROR', null);
      try {
        const stops = await ridershipService.getStops({
          startDay,
          endDay,
          routeIds: routeIds && routeIds.length > 0 ? routeIds : undefined,
          stopIds: stopIds && stopIds.length > 0 ? stopIds : undefined,
          fromTime: fromTime || undefined,
          toTime: toTime || undefined,
          workingTypes,
        });
        if (isLatest()) commit('SET_RAW_STOPS', stops);
      } catch (error) {
        if (isLatest()) commit('SET_ERROR', error.message || 'Failed to fetch stops');
        console.error('Error fetching ridership stops:', error);
      } finally {
        if (isLatest()) commit('SET_LOADING', { key: 'stops', value: false });
      }
    },

    /**
     * Export ridership data as Excel
     */
    async exportExcel(_, { startDay, endDay, routeIds, direction }) {
      if (!startDay || !endDay) return;
      track('export:excel:ridership');

      try {
        await downloadRidershipReport({ startDay, endDay, routeIds, direction });
      } catch (error) {
        console.error('Error exporting ridership excel:', error);
      }
    },

    clearData({ commit }) {
      commit('CLEAR_DATA');
    },
  },

  getters: {
    rawTrips: (state) => state.rawTrips,
    rawStops: (state) => state.rawStops,
    /**
     * `rawStops` with dynamic-route stops collapsed into one row per region
     * (VSER-14220). Chart code should read this instead of `rawStops`.
     */
    regionGroupedStops: (state) => groupStopsByRegion(state.rawStops),
    availableRoutes: (state) => state.availableRoutes,
    availableStops: (state) => state.availableStops,
    routeColorMap: (state) => state.routeColorMap,
    filters: (state) => state.filters,
    loading: (state) => state.loading,
    error: (state) => state.error,
    isLoading: (state) => Object.values(state.loading).some(Boolean),

    /**
     * Ridership by Route – "by Run" view
     * Groups trips by departure hour, stacks by route
     * Returns { categories: string[], series: [{ name, color, data }] }
     */
    routeByRunData: (state) => {
      const { rawTrips, routeColorMap, filters } = state;
      if (!rawTrips.length) return { categories: [], series: [] };

      const useActual = filters.referenceTime === 'actual';

      // Build hour -> route -> totalOn map
      const hourMap = new Map();
      const routeNames = new Set();

      rawTrips.forEach((trip) => {
        const timeStr = useActual ? trip.actualStart || trip.scheduledStart : trip.scheduledStart;
        const hour = extractHour(timeStr);
        const hourLabel = `${String(hour).padStart(2, '0')}:00`;
        const { routeName, totalOn } = trip;

        routeNames.add(routeName);

        if (!hourMap.has(hourLabel)) {
          hourMap.set(hourLabel, {});
        }
        const bucket = hourMap.get(hourLabel);
        bucket[routeName] = (bucket[routeName] || 0) + (totalOn || 0);
      });

      // Sort hours
      const categories = Array.from(hourMap.keys()).sort();
      const sortedRoutes = Array.from(routeNames).sort((a, b) => a.localeCompare(b, 'tr-TR'));

      const series = sortedRoutes.map((name) => ({
        name,
        color: routeColorMap[name] || '#999',
        data: categories.map((h) => hourMap.get(h)?.[name] || 0),
      }));

      return { categories, series };
    },

    /**
     * Ridership by Route – "by Day" view
     * Groups trips by day of week, stacks by route
     */
    routeByDayData: (state) => {
      const { rawTrips, routeColorMap } = state;
      if (!rawTrips.length) return { categories: [], series: [] };

      const dayMap = new Map();
      const routeNames = new Set();

      rawTrips.forEach((trip) => {
        const { dayOfWeek, routeName, totalOn } = trip;
        if (!dayOfWeek) return;
        routeNames.add(routeName);

        if (!dayMap.has(dayOfWeek)) {
          dayMap.set(dayOfWeek, {});
        }
        const bucket = dayMap.get(dayOfWeek);
        bucket[routeName] = (bucket[routeName] || 0) + (totalOn || 0);
      });

      // Sort days
      const categories = Array.from(dayMap.keys()).sort((a, b) => dayIndex(a) - dayIndex(b));
      const sortedRoutes = Array.from(routeNames).sort((a, b) => a.localeCompare(b, 'tr-TR'));

      const series = sortedRoutes.map((name) => ({
        name,
        color: routeColorMap[name] || '#999',
        data: categories.map((d) => dayMap.get(d)?.[name] || 0),
      }));

      // Capitalize day names for display
      const displayCategories = categories.map((d) => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase());

      return { categories: displayCategories, series };
    },

    /**
     * Ridership by Stop – aggregated data
     * Returns sorted array of { stopName, totalOn, totalOff, sum, routes: [...] }
     */
    stopChartData: (state, getters) => {
      const { filters } = state;
      const stops = getters.regionGroupedStops;
      if (!stops.length) return [];

      const { stopMetric, stopSortAsc } = filters;

      const data = stops.map((stop) => ({
        stopId: stop.stopId,
        stopName: stop.stopName,
        totalOn: stop.totalOn || 0,
        totalOff: stop.totalOff || 0,
        sum: (stop.totalOn || 0) + (stop.totalOff || 0),
        routes: (stop.routes || []).map((r) => ({
          routeId: r.routeId,
          routeName: r.routeName,
          totalOn: r.totalOn || 0,
          totalOff: r.totalOff || 0,
        })),
      }));

      // Sort by selected metric
      let sortKey = 'sum';
      if (stopMetric === 'on') sortKey = 'totalOn';
      else if (stopMetric === 'off') sortKey = 'totalOff';
      data.sort((a, b) => (stopSortAsc ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey]));

      return data;
    },

    /**
     * Ridership by Stop – Highcharts series
     * Returns { categories: string[], series: [{ name, color, data }] }
     */
    stopChartSeries: (state, getters) => {
      const stops = getters.stopChartData;
      const { routeColorMap, filters } = state;
      if (!stops.length) return { categories: [], series: [] };

      const { stopMetric } = filters;
      const categories = stops.map((s) => s.stopName);

      // Collect all route names across stops
      const routeNameSet = new Set();
      stops.forEach((stop) => {
        stop.routes.forEach((r) => routeNameSet.add(r.routeName));
      });
      const sortedRoutes = Array.from(routeNameSet).sort((a, b) => a.localeCompare(b, 'tr-TR'));

      const series = sortedRoutes.map((routeName) => ({
        name: routeName,
        color: routeColorMap[routeName] || '#999',
        data: stops.map((stop) => {
          const r = stop.routes.find((rt) => rt.routeName === routeName);
          if (!r) return 0;
          if (stopMetric === 'on') return r.totalOn;
          if (stopMetric === 'off') return r.totalOff;
          return r.totalOn + r.totalOff;
        }),
      }));

      return { categories, series };
    },

    /**
     * Ridership by Route – "by Date" view
     * Groups trips by date with dynamic X-axis granularity:
     *   ≤ 1 day  → hours
     *   ≤ 31 days → daily dates
     *   ≤ 180 days → weekly
     *   > 180 days → monthly
     */
    routeByDateData: (state) => {
      const { rawTrips, routeColorMap, filters } = state;
      if (!rawTrips.length) return { categories: [], series: [], occupancy: [] };

      const { startDay, endDay } = filters.dateRange || {};
      const daySpan = startDay && endDay ? Math.abs(endDay - startDay) : 30;

      // Determine grouping function
      let groupKey;
      if (daySpan <= 1) {
        // Group by hour
        groupKey = (trip) => {
          const useActual = filters.referenceTime === 'actual';
          const timeStr = useActual ? trip.actualStart || trip.scheduledStart : trip.scheduledStart;
          const h = extractHour(timeStr);
          return `${String(h).padStart(2, '0')}:00`;
        };
      } else if (daySpan <= 31) {
        // Group by date
        groupKey = (trip) => trip.date || 'unknown';
      } else if (daySpan <= 180) {
        // Group by ISO week
        groupKey = (trip) => {
          if (!trip.date) return 'unknown';
          const d = new Date(trip.date);
          const dayOfYear = Math.floor((d - new Date(d.getFullYear(), 0, 1)) / 86400000);
          const weekNum = Math.ceil((dayOfYear + 1) / 7);
          return `W${String(weekNum).padStart(2, '0')}`;
        };
      } else {
        // Group by month
        groupKey = (trip) => {
          if (!trip.date) return 'unknown';
          return trip.date.substring(0, 7); // YYYY-MM
        };
      }

      const bucketMap = new Map();
      const routeNames = new Set();
      // For occupancy: bucket -> { totalLoad, count }
      const occupancyMap = new Map();

      rawTrips.forEach((trip) => {
        const key = groupKey(trip);
        const { routeName, totalOn, loadFactor } = trip;
        routeNames.add(routeName);

        if (!bucketMap.has(key)) {
          bucketMap.set(key, {});
        }
        const bucket = bucketMap.get(key);
        bucket[routeName] = (bucket[routeName] || 0) + (totalOn || 0);

        // Track occupancy
        if (!occupancyMap.has(key)) {
          occupancyMap.set(key, { totalLoad: 0, count: 0 });
        }
        const occ = occupancyMap.get(key);
        if (loadFactor != null) {
          occ.totalLoad += loadFactor;
          occ.count += 1;
        }
      });

      const categories = Array.from(bucketMap.keys()).sort();
      const sortedRoutes = Array.from(routeNames).sort((a, b) => a.localeCompare(b, 'tr-TR'));

      const series = sortedRoutes.map((name) => ({
        name,
        color: routeColorMap[name] || '#999',
        data: categories.map((k) => bucketMap.get(k)?.[name] || 0),
      }));

      // Occupancy percentage per bucket
      const occupancy = categories.map((k) => {
        const occ = occupancyMap.get(k);
        if (!occ || occ.count === 0) return 0;
        return Math.round((occ.totalLoad / occ.count) * 100);
      });

      return { categories, series, occupancy };
    },

    /**
     * Check if a single route is selected
     */
    isSingleRouteSelected: (state) => {
      const { routeIds } = state.filters;
      return routeIds.length === 1;
    },

    /**
     * Single-route mode: "by Run" stacks by stops instead of routes
     */
    singleRouteByRunData: (state) => {
      const { rawTrips, filters } = state;
      if (!rawTrips.length) return { categories: [], series: [] };

      const useActual = filters.referenceTime === 'actual';

      // Collect all stop names across trips
      const stopNameSet = new Set();
      const hourMap = new Map();

      rawTrips.forEach((trip) => {
        const timeStr = useActual ? trip.actualStart || trip.scheduledStart : trip.scheduledStart;
        const hour = extractHour(timeStr);
        const hourLabel = `${String(hour).padStart(2, '0')}:00`;

        if (!hourMap.has(hourLabel)) {
          hourMap.set(hourLabel, {});
        }
        const bucket = hourMap.get(hourLabel);

        (trip.stops || []).forEach((stop) => {
          const { stopName, totalOn } = stop;
          stopNameSet.add(stopName);
          bucket[stopName] = (bucket[stopName] || 0) + (totalOn || 0);
        });
      });

      const categories = Array.from(hourMap.keys()).sort();
      const sortedStops = Array.from(stopNameSet);

      // Generate stop colors from a secondary palette
      const STOP_PALETTE = [
        '#5B8FF9',
        '#61DDAA',
        '#F6BD16',
        '#E86452',
        '#6DC8EC',
        '#945FB9',
        '#FF9845',
        '#1E9493',
        '#FF99C3',
        '#269A99',
      ];

      const series = sortedStops.map((name, i) => ({
        name,
        color: STOP_PALETTE[i % STOP_PALETTE.length],
        data: categories.map((h) => hourMap.get(h)?.[name] || 0),
      }));

      return { categories, series };
    },

    /**
     * Single-route mode: "by Day" stacks by stops instead of routes
     */
    singleRouteByDayData: (state) => {
      const { rawTrips } = state;
      if (!rawTrips.length) return { categories: [], series: [] };

      const stopNameSet = new Set();
      const dayMap = new Map();

      rawTrips.forEach((trip) => {
        const { dayOfWeek } = trip;
        if (!dayOfWeek) return;

        if (!dayMap.has(dayOfWeek)) {
          dayMap.set(dayOfWeek, {});
        }
        const bucket = dayMap.get(dayOfWeek);

        (trip.stops || []).forEach((stop) => {
          const { stopName, totalOn } = stop;
          stopNameSet.add(stopName);
          bucket[stopName] = (bucket[stopName] || 0) + (totalOn || 0);
        });
      });

      const categories = Array.from(dayMap.keys()).sort((a, b) => dayIndex(a) - dayIndex(b));
      const displayCategories = categories.map((d) => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase());
      const sortedStops = Array.from(stopNameSet);

      const STOP_PALETTE = [
        '#5B8FF9',
        '#61DDAA',
        '#F6BD16',
        '#E86452',
        '#6DC8EC',
        '#945FB9',
        '#FF9845',
        '#1E9493',
        '#FF99C3',
        '#269A99',
      ];

      const series = sortedStops.map((name, i) => ({
        name,
        color: STOP_PALETTE[i % STOP_PALETTE.length],
        data: categories.map((d) => dayMap.get(d)?.[name] || 0),
      }));

      return { categories: displayCategories, series };
    },

    /**
     * Vehicle occupancy (load factor) per hour bucket for "by Run" view
     * Returns array of percentages matching routeByRunData categories
     */
    occupancyByRunData: (state) => {
      const { rawTrips, filters } = state;
      if (!rawTrips.length) return [];

      const useActual = filters.referenceTime === 'actual';
      const hourMap = new Map();

      rawTrips.forEach((trip) => {
        const timeStr = useActual ? trip.actualStart || trip.scheduledStart : trip.scheduledStart;
        const hour = extractHour(timeStr);
        const hourLabel = `${String(hour).padStart(2, '0')}:00`;

        if (!hourMap.has(hourLabel)) {
          hourMap.set(hourLabel, { totalLoad: 0, count: 0 });
        }
        const bucket = hourMap.get(hourLabel);
        if (trip.loadFactor != null) {
          bucket.totalLoad += trip.loadFactor;
          bucket.count += 1;
        }
      });

      const categories = Array.from(hourMap.keys()).sort();
      return categories.map((h) => {
        const occ = hourMap.get(h);
        if (!occ || occ.count === 0) return 0;
        return Math.round((occ.totalLoad / occ.count) * 100);
      });
    },

    /**
     * Vehicle occupancy per day for "by Day" view
     */
    occupancyByDayData: (state) => {
      const { rawTrips } = state;
      if (!rawTrips.length) return [];

      const dayMap = new Map();

      rawTrips.forEach((trip) => {
        const { dayOfWeek, loadFactor } = trip;
        if (!dayOfWeek) return;

        if (!dayMap.has(dayOfWeek)) {
          dayMap.set(dayOfWeek, { totalLoad: 0, count: 0 });
        }
        const bucket = dayMap.get(dayOfWeek);
        if (loadFactor != null) {
          bucket.totalLoad += loadFactor;
          bucket.count += 1;
        }
      });

      const categories = Array.from(dayMap.keys()).sort((a, b) => dayIndex(a) - dayIndex(b));
      return categories.map((d) => {
        const occ = dayMap.get(d);
        if (!occ || occ.count === 0) return 0;
        return Math.round((occ.totalLoad / occ.count) * 100);
      });
    },

    /**
     * Unique direction values derived from available routes
     */
    availableDirections: (state) => {
      const directions = new Set();
      state.availableRoutes.forEach((r) => {
        if (r.direction) directions.add(r.direction);
      });
      return Array.from(directions);
    },
  },
};

/* eslint-enable no-param-reassign */
